"use client";

import { useId, type ReactNode } from "react";
import type { DevicePermission } from "@/contexts/studio-context";
import { FONT_MONO, SET } from "./palette";

// ──────────────────────────────────────────────────────────────
// Section header, kicker on the left, optional trailing slot
// (badge + rescan link). Mirrors the mockup pattern used for
// "Entrées MIDI", "Détection par micro", "Sortie audio", "Divers".
// ──────────────────────────────────────────────────────────────

interface SectionHeaderProps {
  kicker: string;
  /** Right-aligned content (badge, switch, rescan link…). Layout is the caller's responsibility. */
  trailing?: ReactNode;
}

export function SectionHeader({ kicker, trailing }: SectionHeaderProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 14,
        minHeight: 22,
      }}
    >
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          letterSpacing: "0.28em",
          color: SET.violetLight,
          textTransform: "uppercase",
          fontWeight: 500,
        }}
      >
        {kicker}
      </span>

      {trailing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {trailing}
        </div>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Group, visually delimited block within a section. The mockup
// uses a thin surface card behind each section's content.
// ──────────────────────────────────────────────────────────────

interface GroupProps {
  children: ReactNode;
}

export function Group({ children }: GroupProps) {
  return (
    <div
      style={{
        background: SET.surface,
        border: `1px solid ${SET.border}`,
        borderRadius: 12,
        padding: "8px 4px",
      }}
    >
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Line, single setting row. Label on the left, control on the right.
// ──────────────────────────────────────────────────────────────

interface LineProps {
  label: ReactNode;
  hint?: ReactNode;
  control: ReactNode;
  /** First/last lines in a Group skip the divider for a cleaner edge. */
  withDivider?: boolean;
}

export function Line({ label, hint, control, withDivider = true }: LineProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "10px 14px",
        borderTop: withDivider ? `1px solid ${SET.border}` : "none",
        minHeight: 44,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 13, color: SET.text, fontWeight: 400 }}>{label}</span>
        {hint ? (
          <span style={{ fontSize: 11, color: SET.textMute }}>{hint}</span>
        ) : null}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        {control}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Switch, accessible on/off toggle. Used for global mic, per-device
// MIDI routing, etc. Same violet tint as the rest of the overlay.
// ──────────────────────────────────────────────────────────────

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label?: string;
}

export function Switch({ checked, onChange, disabled, label }: SwitchProps) {
  const id = useId();

  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        position: "relative",
        width: 36,
        height: 20,
        borderRadius: 999,
        border: `1px solid ${checked ? SET.violetGlow : SET.borderHi}`,
        background: checked ? SET.violetSoft : "rgba(255,255,255,0.04)",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 150ms, border-color 150ms",
        opacity: disabled ? 0.5 : 1,
        padding: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 18 : 2,
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: checked ? SET.violet : "rgba(255,255,255,0.45)",
          boxShadow: checked ? `0 0 8px ${SET.violetGlow}` : "none",
          transition: "left 150ms ease, background 150ms",
        }}
      />
    </button>
  );
}

// ──────────────────────────────────────────────────────────────
// Slider, pointer-driven horizontal slider. Shared by the volume
// and sensitivity rows. Behavior aligned with `volume-slider.tsx`.
// ──────────────────────────────────────────────────────────────

interface SliderProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  width?: number;
  disabled?: boolean;
}

export function Slider({
  value,
  min = 0,
  max = 100,
  onChange,
  width = 140,
  disabled,
}: SliderProps) {
  const ratio = Math.max(0, Math.min(1, (value - min) / (max - min)));

  const handlePointer = (clientX: number, rect: DOMRect) => {
    const r = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const next = Math.round(min + r * (max - min));
    onChange(next);
  };

  return (
    <div
      style={{
        position: "relative",
        width,
        height: 16,
        display: "flex",
        alignItems: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
      onPointerDown={(e) => {
        if (disabled) {
          return;
        }

        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        handlePointer(e.clientX, rect);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (disabled || e.buttons === 0) {
          return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        handlePointer(e.clientX, rect);
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          height: 3,
          borderRadius: 999,
          background: "rgba(255,255,255,0.08)",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${ratio * 100}%`,
            background: SET.violet,
            borderRadius: 999,
            boxShadow: `0 0 6px ${SET.violetGlow}`,
          }}
        />
      </div>
      <div
        style={{
          position: "absolute",
          left: `${ratio * 100}%`,
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: SET.violetLight,
          border: `1px solid ${SET.violet}`,
          transform: "translateX(-50%)",
          boxShadow: `0 0 8px ${SET.violetGlow}`,
        }}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Permission badge, colored pill mirroring browser permission state.
// ──────────────────────────────────────────────────────────────

interface PermBadgeProps {
  permission: DevicePermission;
  /** Action label for the "prompt" / "denied" states (e.g. "Autoriser"). */
  onRequest?: () => void;
}

const PERM_LABEL: Record<DevicePermission, string> = {
  granted: "Autorisé",
  prompt: "Autoriser",
  denied: "Refusé",
  unsupported: "Indisponible",
};

export function PermBadge({ permission, onRequest }: PermBadgeProps) {
  const isGranted = permission === "granted";
  const isPrompt = permission === "prompt";
  const isDenied = permission === "denied";
  const clickable = !isGranted && permission !== "unsupported" && onRequest !== undefined;

  const color = isGranted
    ? SET.green
    : isDenied
      ? SET.red
      : isPrompt
        ? SET.violetLight
        : SET.textMute;
  const dot = isGranted ? SET.green : isDenied ? SET.red : isPrompt ? SET.violet : SET.textMute;
  const glow = isGranted
    ? SET.greenGlow
    : isDenied
      ? "rgba(248,113,113,0.45)"
      : isPrompt
        ? SET.violetGlow
        : "transparent";

  const content = (
    <>
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: dot,
          boxShadow: glow !== "transparent" ? `0 0 6px ${glow}` : "none",
        }}
      />
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          letterSpacing: "0.18em",
          color,
          textTransform: "uppercase",
        }}
      >
        {PERM_LABEL[permission]}
      </span>
    </>
  );

  const baseStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px",
    borderRadius: 999,
    border: `1px solid ${SET.border}`,
    background: SET.surface,
    fontFamily: FONT_MONO,
    fontSize: 10,
  };

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onRequest}
        style={{ ...baseStyle, cursor: "pointer" }}
      >
        {content}
      </button>
    );
  }

  return <span style={baseStyle}>{content}</span>;
}

// ──────────────────────────────────────────────────────────────
// Inline link, used for the "↻ rescan" affordance.
// ──────────────────────────────────────────────────────────────

interface InlineLinkProps {
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}

export function InlineLink({ onClick, children, disabled }: InlineLinkProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: "transparent",
        border: "none",
        color: disabled ? SET.textVeryMute : SET.textDim,
        fontFamily: FONT_MONO,
        fontSize: 11,
        letterSpacing: "0.06em",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: 0,
        textTransform: "uppercase",
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.color = SET.text;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.color = SET.textDim;
        }
      }}
    >
      {children}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────
// Stub picker, non-interactive trigger for V1 device dropdowns
// (audio source / output picker). Real enumeration lands later.
// ──────────────────────────────────────────────────────────────

interface StubPickerProps {
  label: string;
}

export function StubPicker({ label }: StubPickerProps) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 10px",
        borderRadius: 8,
        border: `1px solid ${SET.border}`,
        background: SET.surface,
        color: SET.textDim,
        fontSize: 12,
        cursor: "default",
      }}
      aria-disabled
    >
      {label}
      <span aria-hidden style={{ color: SET.textVeryMute, fontSize: 10 }}>
        ▾
      </span>
    </span>
  );
}
